import React, { useMemo } from "react";
import {
    Box,
    Button,
    Chip,
    Divider,
    Stack,
    Typography,
    TableContainer,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import {
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Check as CheckIcon,
} from '@mui/icons-material';
import { DiaryCreatedSource, DiaryItemStatus, DiaryItemType } from "../../models/diaryTypes";
import type { DiaryItemDto } from "../../models/diaryTypes";
import type { PaymentMilestoneDisplay } from "../../models/Payments";

export type EntryAction = 'view' | 'edit' | 'complete' ;

type Props = {
    children: DiaryItemDto[];

    onAddLinkedTask: () => void;
    onEditChild: (child: DiaryItemDto) => void;
    onAction: (action: EntryAction, entry: DiaryItemDto | number) => void;

    title?: string;

    paymentCurrencyCode?: string;
    milestoneByDiaryItemId?: Record<number, PaymentMilestoneDisplay>;

    onCompleteChild?: (childId: number) => Promise<void> | void;

    viewOnly?: boolean;
};

interface EntryActionsProps {
    entry: DiaryItemDto;
    onAction: (action: EntryAction, entry: DiaryItemDto | number) => void;
    disabled?: boolean;
    canPay?: boolean;
}

const EntryActions: React.FC<EntryActionsProps> = ({ entry, onAction, disabled = false, canPay = false }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (event?: React.MouseEvent) => {
        event?.stopPropagation();
        setAnchorEl(null);
    };

    const handleAction = (action: EntryAction) => {
        handleClose();
        if (action === 'complete') {
            onAction(action, entry.id);
        } else {
            onAction(action, entry);
        }
    };

    // Determine which actions are available based on entry status
    const isTask = entry.type === DiaryItemType.Task;
    const isCompletedOrCancelled = entry.status == DiaryItemStatus.Completed || entry.status == DiaryItemStatus.Cancelled;

    return (
        <>
            <IconButton
                size="small"
                onClick={handleClick}
                disabled={disabled}
                sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {/* Edit action - always available except for cancelled events */}
                {!isCompletedOrCancelled && (
                    <MenuItem onClick={() => handleAction('edit')}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                )}

                {isCompletedOrCancelled && (
                    <MenuItem onClick={() => handleAction('view')}>
                        <ListItemIcon>
                            <ImportContactsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>View</ListItemText>
                    </MenuItem>
                )}

                {/* Complete action - only for incomplete tasks */}
                {isTask && !isCompletedOrCancelled && canPay && (
                    <MenuItem onClick={() => handleAction('complete')}>
                        <ListItemIcon>
                            <CheckIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText>Mark Complete</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

function getAnchorUtc(child: DiaryItemDto): string | null {
    return child.type === DiaryItemType.Task
        ? (child.dueUtc ?? null)
        : (child.startUtc ?? null);
}

function sortByAnchor(a: DiaryItemDto, b: DiaryItemDto) {
    const ax = getAnchorUtc(a);
    const bx = getAnchorUtc(b);
    const at = ax ? new Date(ax).getTime() : 0;
    const bt = bx ? new Date(bx).getTime() : 0;
    return at - bt;
}

function formatWhen(child: DiaryItemDto) {
    const anchor = getAnchorUtc(child);
    if (!anchor) return "-";
    const dt = new Date(anchor);
    //return dt.toLocaleString(); 
    return dt.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

function statusChip(status: DiaryItemStatus) {
    switch (status) {
        case DiaryItemStatus.Planned:
            return <Chip size="small" label="Planned" variant="outlined" />;
        case DiaryItemStatus.Completed:
            return <Chip size="small" label="Completed" variant="outlined" />;
        case DiaryItemStatus.Cancelled:
            return <Chip size="small" label="Cancelled" variant="outlined" />;
        default:
            return <Chip size="small" label="-" variant="outlined" />;
    }
}

function typeChip(type: DiaryItemType) {
    return type === DiaryItemType.Task
        ? <Chip size="small" label="Task" variant="outlined" />
        : <Chip size="small" label="Event" variant="outlined" />;
}

export default function DiaryChildrenPanel({
    children,
    onAddLinkedTask,
    onEditChild,
    onAction,
    title = "Related items",
    paymentCurrencyCode,
    milestoneByDiaryItemId,
    onCompleteChild,
    viewOnly
}: Props) {
    const { manualChildren, generatedChildren } = useMemo(() => {
        const manual = children
            .filter(c => c.createdSource === DiaryCreatedSource.User)
            .sort(sortByAnchor);

        const generated = children
            .filter(c => c.createdSource === DiaryCreatedSource.System)
            .sort(sortByAnchor);

        return { manualChildren: manual, generatedChildren: generated };
    }, [children]);

    const showPayment = !!paymentCurrencyCode && !!milestoneByDiaryItemId;

    const disableForm = viewOnly === true ?? false;

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {title}
            </Typography>

            {/* Linked tasks (manual) */}
            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Linked tasks
                    </Typography>

                    <Button startIcon={<AddIcon />} variant="outlined" onClick={onAddLinkedTask} disabled={disableForm}>
                        Add linked task
                    </Button>
                </Stack>

                {manualChildren.length === 0 ? (
                    <Typography color="text.secondary">No linked tasks.</Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 110 }}>Type</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell sx={{ width: 220 }}>When</TableCell>
                                <TableCell sx={{ width: 120 }}>Status</TableCell>
                                <TableCell sx={{ width: 80 }} align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {manualChildren.map(c => (
                                <TableRow key={c.id} hover>
                                    <TableCell>{typeChip(c.type)}</TableCell>
                                    <TableCell>{c.title}</TableCell>
                                    <TableCell>{formatWhen(c)}</TableCell>
                                    <TableCell>{statusChip(c.status)}</TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            startIcon={<EditOutlinedIcon />}
                                            onClick={() => onEditChild(c)}
                                        >
                                            Edit
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            <Divider />

            {/* Linked tasks (generated) */}
            <Box>
                {generatedChildren.length === 0 ? (
                    <></>
                ) : (
                    <>
                            <Typography color="text.secondary">Generated tasks</Typography>

                            <TableContainer sx={{ overflowX: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    width: 110,
                                                    display: { xs: "none", sm: "table-cell" }
                                                }}
                                            >
                                                Type
                                            </TableCell>

                                            <TableCell sx={{ width: 220 }}>
                                                Title
                                            </TableCell>

                                            {showPayment && (
                                                <TableCell sx={{ width: 200 }}>
                                                    Amount
                                                </TableCell>
                                            )}

                                            <TableCell sx={{ width: 140 }}>
                                                When
                                            </TableCell>

                                            <TableCell sx={{ width: 100 }}>
                                                Status
                                            </TableCell>

                                            <TableCell sx={{ width: 80 }} align="right">
                                                Action
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {generatedChildren.map(c => {
                                            const ms = showPayment ? milestoneByDiaryItemId?.[c.id] : undefined;

                                            const canPayAndComplete =
                                                !!ms &&
                                                ms.amountOutstanding > 0 &&
                                                c.status !== DiaryItemStatus.Completed &&
                                                !!onCompleteChild;

                                            return (
                                                <TableRow key={c.id} hover>
                                                    <TableCell
                                                        sx={{
                                                            display: { xs: "none", sm: "table-cell" }
                                                        }}
                                                    >
                                                        {typeChip(c.type)}
                                                    </TableCell>

                                                    <TableCell>
                                                        {c.title}
                                                    </TableCell>

                                                    {showPayment && (
                                                        <TableCell>
                                                            {ms ? (
                                                                <Stack spacing={0.25}>
                                                                    <Typography variant="body2">
                                                                        {ms.amount.toFixed(2)} {paymentCurrencyCode}
                                                                    </Typography>
                                                                    {canPayAndComplete && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Outstanding
                                                                        </Typography>
                                                                    )}
                                                                    {!canPayAndComplete && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Paid
                                                                        </Typography>
                                                                    )}
                                                                </Stack>
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    —
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    )}

                                                    <TableCell>
                                                        {formatWhen(c)}
                                                    </TableCell>

                                                    <TableCell>
                                                        {statusChip(c.status)}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        <EntryActions
                                                            entry={c}
                                                            onAction={onAction}
                                                            canPay={canPayAndComplete}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                        </>
                )}
            </Box>
        </Stack>
    );
}
